import React, { useState } from 'react';
import { ToolApproval } from '../../types';
import { Icons } from './Icons';

// --- Base Styled Components ---

interface ActionCardWrapperProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

export const ActionCardWrapper: React.FC<ActionCardWrapperProps & { status?: string, onToggleExpand?: () => void, isExpanded?: boolean }> = ({ title, icon, children, status = 'pending', onToggleExpand, isExpanded = true }) => {
  const isExecuting = status === 'executing' || status === 'loading' || status === 'approved';
  const isCompleted = status === 'executed' || status === 'completed';
  const isRejected = status === 'rejected';

  return (
    <div 
      className={`bg-white dark:bg-[#212121] border border-[#e5e5e5] dark:border-gray-800 rounded-[12px] mt-4 w-full transition-all duration-300 overflow-hidden ${isRejected ? 'opacity-70 grayscale-[0.5]' : ''}`}
      style={{ boxShadow: '0 8px 20px rgba(0,0,0,0.08)', width: '100%', maxWidth: '720px', margin: '16px auto' }}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50 dark:border-gray-800">
        <div className="flex items-center gap-2">
          {isCompleted && <Icons.Check className="w-4 h-4 text-black dark:text-white" />}
          <div className="text-black dark:text-white w-5 h-5 flex items-center justify-center">
            {isExecuting ? <div className="w-4 h-4 border-2 border-gray-200 dark:border-gray-700 border-t-[#202124] dark:border-t-white rounded-full animate-spin" /> : 
             icon}
          </div>
          <h3 className="font-bold text-black dark:text-white text-base">{title}</h3>
          
          <span className={`text-[11px] font-bold px-3 py-0.5 rounded-full ${
            isExecuting ? 'bg-gray-100 dark:bg-gray-800 text-black dark:text-white animate-pulse' :
            isCompleted ? 'bg-gray-100 dark:bg-gray-800 text-black dark:text-white' :
            isRejected ? 'bg-gray-200 dark:bg-gray-900 text-gray-700 dark:text-gray-400' :
            'bg-gray-55 dark:bg-gray-900 text-black dark:text-white'
          }`}>
            {isExecuting ? 'Processing...' : 
             status === 'executed' || status === 'completed' ? 'Complete' : 
             status === 'rejected' ? 'Rejected' : 
             'Pending'}
          </span>
        </div>

        {onToggleExpand && (
            <button 
                onClick={onToggleExpand}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                title={isExpanded ? "Collapse Details" : "Show Details"}
            >
                <Icons.ChevronDown className={`w-5 h-5 text-black dark:text-white transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
        )}
      </div>
      
      <div className={`w-full transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100 p-5' : 'max-h-0 opacity-0 pointer-events-none'}`}>
        {children}
      </div>
    </div>
  );
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
}

export const Button: React.FC<ButtonProps> = ({ variant = 'primary', className = '', children, ...props }) => {
  const base = "rounded-lg px-4 py-2 font-medium transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-100",
    secondary: "bg-transparent border border-gray-300 dark:border-gray-700 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#171717]",
    danger: "bg-transparent border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30",
    ghost: "bg-transparent text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 border-none"
  };
  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = '', ...props }) => (
  <input 
    className={`w-full bg-white dark:bg-[#050505] border border-[#e5e5e5] dark:border-white/10 rounded-lg px-3 py-2 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white transition-colors ${className}`}
    {...props}
  />
);

export const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className = '', ...props }) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  React.useEffect(() => {
    adjustHeight();
  }, [props.value]);

  return (
    <textarea 
      ref={textareaRef}
      onInput={adjustHeight}
      className={`w-full bg-white dark:bg-[#050505] border border-[#e5e5e5] dark:border-white/10 rounded-lg px-3 py-2 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white transition-colors resize-none overflow-hidden min-h-[100px] ${className}`}
      {...props}
    />
  );
};

// --- Helpers ---

// ─── URL Linkifier ───
const Linkify: React.FC<{ text: string }> = ({ text }) => {
  if (!text) return null;
  const URL_REGEX = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) => {
        if (part.match(URL_REGEX)) {
          const cleanUrl = part.replace(/[.,!;]$/, '');
          const trailing = part.length > cleanUrl.length ? part.slice(cleanUrl.length) : '';
          
          let AppIcon = null;
          if (cleanUrl.includes('docs.google.com/document')) AppIcon = Icons.GoogleDocs;
          else if (cleanUrl.includes('docs.google.com/spreadsheets')) AppIcon = Icons.GoogleSheets;
          else if (cleanUrl.includes('calendar.google.com')) AppIcon = Icons.GoogleCalendar;
          else if (cleanUrl.includes('notion.so')) AppIcon = Icons.Notion;
          
          return (
            <React.Fragment key={i}>
              <a 
                href={cleanUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline decoration-blue-200 dark:decoration-blue-800 hover:decoration-blue-500 underline-offset-4 inline-flex items-center gap-1.5 font-medium transition-all px-1.5 py-0.5 rounded-md hover:bg-blue-50/50 dark:hover:bg-blue-950/20"
              >
                {AppIcon && <AppIcon className="w-4 h-4" />}
                {cleanUrl}
                <svg className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              {trailing}
            </React.Fragment>
          );
        }
        return part;
      })}
    </>
  );
};

const formatDateForInput = (dateStr: string) => {
  if (!dateStr) return '';
  // If it's already YYYY-MM-DD, return it
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  
  // Try to parse M/D/YYYY
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    const [_, m, d, y] = match;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  
  // Last resort, try native Date parsing
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.toISOString().split('T')[0];
  }
  return dateStr;
};

// ─── Parameter Filtering ───
// Technical/internal parameters that should never be shown to end users
const HIDDEN_PARAMS = new Set([
  'user_id', 'userId', 'uid',
  'body_length', 'bodyLength',
  'max_results', 'maxResults', 'limit',
  'access_token', 'token', 'api_key', 'apiKey',
  'refresh_token', 'session_id', 'sessionId',
  'page_token', 'pageToken', 'offset',
  'include_spam_trash', 'includeSpamTrash',
  'format', 'response_format',
  'label_ids', 'labelIds',
  'raw', 'fields', 'part',
  'workspace_id', 'workspaceId',
  'thread_id', 'threadId',
  'message_id', 'messageId',
  'calendar_id', 'calendarId',
  'document_id', 'documentId', 'doc_id', 'docId',
  'spreadsheet_id', 'spreadsheetId', 'sheet_id', 'sheetId',
  'file_id', 'fileId',
  'parent_id', 'parentId', 'folder_id', 'folderId',
  'mime_type', 'mimeType',
  'order_by', 'orderBy', 'sort_order', 'sortOrder',
  'include_deleted', 'includeDeleted',
  'single_events', 'singleEvents',
  'time_min', 'timeMin', 'time_max', 'timeMax',
  'q', // internal query param
  'corpora', 'spaces',
  'supports_all_drives', 'supportsAllDrives',
  'include_items_from_all_drives', 'includeItemsFromAllDrives',
]);

// Friendly display names for common parameters
const FRIENDLY_LABELS: Record<string, string> = {
  'to': 'Recipient',
  'from': 'From',
  'subject': 'Subject',
  'body': 'Message',
  'message': 'Message',
  'content': 'Content',
  'text': 'Content',
  'title': 'Title',
  'summary': 'Title',
  'name': 'Name',
  'description': 'Description',
  'location': 'Location',
  'date': 'Date',
  'time': 'Time',
  'start_time': 'Start Time',
  'end_time': 'End Time',
  'start': 'Start',
  'end': 'End',
  'recurrence': 'Repeat',
  'attendees': 'Attendees',
  'email': 'Email',
  'recipient': 'Recipient',
  'send_at': 'Scheduled For',
  'initial_text': 'Content',
  'query': 'Search',
  'search_query': 'Search',
  'range': 'Cell Range',
  'sheet_name': 'Sheet Name',
  'values': 'Data',
  'url': 'Link',
  'link': 'Link',
};

// Get clean, user-friendly label for a parameter key
const getFriendlyLabel = (key: string): string => {
  const lower = key.toLowerCase();
  if (FRIENDLY_LABELS[lower]) return FRIENDLY_LABELS[lower];
  // Convert snake_case / camelCase to Title Case
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
};

// Filter args to only user-relevant ones
const filterVisibleArgs = (args: Record<string, any>): [string, any][] => {
  return Object.entries(args || {}).filter(([key, value]) => {
    // Skip hidden params
    if (HIDDEN_PARAMS.has(key)) return false;
    // Skip null/undefined/empty values
    if (value === null || value === undefined || value === '') return false;
    // Skip boolean false values (usually config flags)
    if (value === false) return false;
    // Skip values that look like UUIDs or tokens
    if (typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value)) return false;
    return true;
  });
};

// ─── Parameter Importance Classification ───
// Classifies parameters by how important they are for the user to review before approving
type ParamImportance = 'critical' | 'important' | 'info';

const PARAM_IMPORTANCE: Record<string, ParamImportance> = {
  // Critical — who/what is affected, irreversible targets
  'to': 'critical', 'recipient': 'critical', 'email': 'critical',
  'title': 'critical', 'summary': 'critical', 'name': 'critical',
  'attendees': 'critical',
  // Important — the content/payload being sent or key details
  'subject': 'important', 'body': 'important', 'message': 'important',
  'content': 'important', 'text': 'important', 'initial_text': 'important',
  'description': 'important', 'location': 'important',
  'date': 'important', 'time': 'important',
  'start_time': 'important', 'end_time': 'important',
  'start': 'important', 'end': 'important',
  'send_at': 'important', 'recurrence': 'important',
  // Info — secondary details
  'query': 'info', 'search_query': 'info', 'range': 'info',
  'sheet_name': 'info', 'values': 'info', 'url': 'info', 'link': 'info',
  'from': 'info',
};

const getParamImportance = (key: string): ParamImportance => {
  const lower = key.toLowerCase();
  if (PARAM_IMPORTANCE[lower]) return PARAM_IMPORTANCE[lower];
  // Heuristic: keys containing these words are likely important
  if (lower.includes('recipient') || lower.includes('target') || lower.includes('email')) return 'critical';
  if (lower.includes('body') || lower.includes('content') || lower.includes('message')) return 'important';
  return 'info';
};

// Sort visible args by importance: critical first, then important, then info
const classifyVisibleArgs = (args: Record<string, any>): { importance: ParamImportance; key: string; value: any }[] => {
  const visible = filterVisibleArgs(args);
  const order: Record<ParamImportance, number> = { critical: 0, important: 1, info: 2 };
  return visible
    .map(([key, value]) => ({ importance: getParamImportance(key), key, value }))
    .sort((a, b) => order[a.importance] - order[b.importance]);
};

// Generate a human-readable one-line summary of what the action will do
const getActionSummary = (toolName: string, args: Record<string, any>): string | null => {
  const t = (toolName || '').toLowerCase();
  const to = args.to || args.recipient || args.email || '';
  const title = args.title || args.summary || args.name || '';
  const subject = args.subject || '';

  if ((t.includes('send') || t.includes('gmail')) && t.includes('email') || t === 'gmail_send' || t === 'send_email') {
    if (to && subject) return `Send email "${subject}" to ${to}`;
    if (to) return `Send an email to ${to}`;
    return 'Send an email';
  }
  if (t.includes('schedule') && (t.includes('email') || t.includes('gmail'))) {
    if (to) return `Schedule email to ${to}`;
    return 'Schedule an email for later';
  }
  if (t.includes('create') && (t.includes('event') || t.includes('calendar'))) {
    if (title) return `Create event "${title}"`;
    return 'Create a calendar event';
  }
  if (t.includes('delete')) {
    if (title) return `Delete "${title}"`;
    return `Delete ${t.replace(/_/g, ' ').replace('search and ', '')}`;
  }
  if (t.includes('create') && (t.includes('doc') || t.includes('spreadsheet'))) {
    if (title) return `Create "${title}"`;
    return `Create a new ${t.includes('spreadsheet') ? 'spreadsheet' : 'document'}`;
  }
  if (t.includes('update') || t.includes('write') || t.includes('append')) {
    if (title) return `Update "${title}"`;
    return `Update ${t.replace(/_/g, ' ')}`;
  }
  if (t.includes('share')) {
    if (to) return `Share with ${to}`;
    return 'Share a file';
  }
  // Read/list operations
  if (t.includes('list') || t.includes('search') || t.includes('get') || t.includes('read') || t.includes('scan')) {
    return null; // No summary needed for read operations — they're low-risk
  }
  return null;
};

// ─── Execution Result Formatter ───
// Parse raw execution results into a clean, user-friendly display
const ExecutionResultDisplay: React.FC<{ result: string, toolName: string }> = ({ result, toolName }) => {
  if (!result) return null;

  // Try to parse as JSON first
  let parsed: any = null;
  try {
    parsed = JSON.parse(result);
  } catch (e) {
    // Not JSON — render as clean text
  }

  // Case 1: Parsed JSON object with meaningful fields
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const displayEntries = filterVisibleArgs(parsed);
    
    // If it has a count + items (e.g. {count: 9, emails: [...]})
    const countKey = Object.keys(parsed).find(k => k === 'count' || k === 'total' || k === 'total_count');
    const listKey = Object.keys(parsed).find(k => Array.isArray(parsed[k]));
    
    if (countKey !== undefined && parsed[countKey] !== undefined) {
      const itemType = listKey ? listKey.replace(/_/g, ' ') : 'items';
      return (
        <div className="mt-4 bg-gray-50/80 dark:bg-[#0a0a0a]/60 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-sm">
              <Icons.Check className="w-4 h-4 text-black dark:text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#202124] dark:text-[#ececec]">
                Found {parsed[countKey]} {itemType}
              </p>
              {parsed.message && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{String(parsed.message)}</p>
              )}
            </div>
          </div>
          {displayEntries.length > 0 && displayEntries.filter(([k]) => k !== countKey && k !== listKey && k !== 'message').length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-2">
              {displayEntries.filter(([k]) => k !== countKey && k !== listKey && k !== 'message').map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">{getFriendlyLabel(key)}:</span>
                  <span className="text-[#202124] dark:text-[#ececec] font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Simple success result with a message
    if (parsed.message || parsed.status || parsed.success !== undefined) {
      const isSuccess = parsed.success !== false && parsed.status !== 'error' && parsed.status !== 'failed';
      return (
        <div className="mt-4 bg-gray-50/80 dark:bg-[#0a0a0a]/60 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-sm ${
              isSuccess 
                ? 'bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-700' 
                : 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800'
            }`}>
              {isSuccess 
                ? <Icons.Check className="w-4 h-4 text-black dark:text-white" />
                : <Icons.ShieldAlert className="w-4 h-4 text-red-500" />
              }
            </div>
            <div>
              <p className={`text-sm font-semibold ${isSuccess ? 'text-[#202124] dark:text-[#ececec]' : 'text-red-600 dark:text-red-400'}`}>
                {parsed.message || (isSuccess ? 'Action completed successfully' : 'Action failed')}
              </p>
              {parsed.status && parsed.status !== 'success' && parsed.status !== 'error' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Status: {parsed.status}</p>
              )}
            </div>
          </div>
          {displayEntries.filter(([k]) => !['message', 'status', 'success', 'error'].includes(k)).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-2">
              {displayEntries.filter(([k]) => !['message', 'status', 'success', 'error'].includes(k)).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">{getFriendlyLabel(key)}:</span>
                  <span className="text-[#202124] dark:text-[#ececec] font-medium truncate max-w-[400px]">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Generic object — show filtered key-value pairs cleanly
    if (displayEntries.length > 0) {
      return (
        <div className="mt-4 bg-gray-50/80 dark:bg-[#0a0a0a]/60 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2 mb-3">
            <Icons.Check className="w-4 h-4 text-black dark:text-white" />
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Result</span>
          </div>
          <div className="space-y-2.5">
            {displayEntries.slice(0, 8).map(([key, value]) => (
              <div key={key} className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 dark:text-gray-400 font-medium shrink-0 min-w-[80px]">{getFriendlyLabel(key)}</span>
                <span className="text-[#202124] dark:text-[#ececec] break-words">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
              </div>
            ))}
            {displayEntries.length > 8 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">+ {displayEntries.length - 8} more fields</p>
            )}
          </div>
        </div>
      );
    }
  }

  // Case 2: Parsed JSON array
  if (parsed && Array.isArray(parsed)) {
    return (
      <div className="mt-4 bg-gray-50/80 dark:bg-[#0a0a0a]/60 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-sm">
            <Icons.Check className="w-4 h-4 text-black dark:text-white" />
          </div>
          <p className="text-sm font-semibold text-[#202124] dark:text-[#ececec]">
            Retrieved {parsed.length} {parsed.length === 1 ? 'result' : 'results'}
          </p>
        </div>
      </div>
    );
  }

  // Case 3: Plain text result — show cleanly
  const cleanResult = result
    .replace(/^Execution Result:\s*/i, '')
    .replace(/^Success:\s*/i, '')
    .trim();

  if (!cleanResult) return null;

  // If it's a short message, show as a success banner
  if (cleanResult.length < 200 && !cleanResult.includes('{')) {
    return (
      <div className="mt-4 bg-gray-50/80 dark:bg-[#0a0a0a]/60 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-sm">
            <Icons.Check className="w-4 h-4 text-black dark:text-white" />
          </div>
          <p className="text-sm font-medium text-[#202124] dark:text-[#ececec]">{cleanResult}</p>
        </div>
      </div>
    );
  }

  // Longer text — show in a collapsible area
  return <CollapsibleResult text={cleanResult} />;
};

// Collapsible raw result for long outputs
const CollapsibleResult: React.FC<{ text: string }> = ({ text }) => {
  const [isOpen, setIsOpen] = useState(false);
  const preview = text.length > 120 ? text.substring(0, 120) + '...' : text;

  return (
    <div className="mt-4 bg-gray-50/80 dark:bg-[#0a0a0a]/60 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100/50 dark:hover:bg-[#171717]/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icons.Check className="w-4 h-4 text-black dark:text-white" />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Result Details</span>
        </div>
        <Icons.ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {!isOpen && (
        <p className="px-4 pb-3 text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{preview}</p>
      )}
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800">
          <div className="mt-3 text-[12px] font-mono text-[#202124] dark:text-[#ececec] whitespace-pre-wrap overflow-auto max-h-[300px] leading-relaxed">
            <Linkify text={text} />
          </div>
        </div>
      )}
    </div>
  );
};

// --- Specific Cards ---

import { api } from '../../services/api.ts';

interface SpecificCardProps {
  toolApproval: ToolApproval;
  onApprove?: (approval: ToolApproval, args: any) => void;
  onReject?: (approval: ToolApproval) => void;
  onModify?: (text: string) => void; // Optional fallback
}

// 1. Email Send Card
export const EmailSendCard: React.FC<SpecificCardProps> = ({ toolApproval, onApprove, onReject }) => {
  const [to, setTo] = useState(toolApproval.args.to || toolApproval.args.recipient || toolApproval.args.email || '');
  const [subject, setSubject] = useState(toolApproval.args.subject || toolApproval.args.title || '');
  const [body, setBody] = useState(toolApproval.args.body || toolApproval.args.message || toolApproval.args.content || toolApproval.args.text || '');
  const [modText, setModText] = useState('');
  const [showMod, setShowMod] = useState(false);
  const [isModifying, setIsModifying] = useState(false);

  const [isExpanded, setIsExpanded] = useState(toolApproval.status === 'pending');
  const isTerminal = toolApproval.status === 'executed' || toolApproval.status === 'rejected' || toolApproval.status === 'approved';

  const handleInlineModify = async () => {
    if (!modText.trim()) return;
    setIsModifying(true);
    try {
      const newArgs = await api.modifyCardParams(toolApproval.toolName, { to, subject, body }, modText);
      if (newArgs.to !== undefined) setTo(newArgs.to);
      if (newArgs.subject !== undefined) setSubject(newArgs.subject);
      if (newArgs.body !== undefined) setBody(newArgs.body);
      setModText('');
      setShowMod(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsModifying(false);
    }
  };

  return (
    <ActionCardWrapper 
        title="Send Email" 
        icon={<Icons.Gmail />} 
        status={toolApproval.status} 
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
    >
      <div className={`space-y-3 mb-6 transition-all duration-300 ${isModifying ? "opacity-50 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-3">
          <span className="text-sm text-black dark:text-white w-12 shrink-0">To:</span>
          <Input value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@example.com" disabled={isModifying || isTerminal} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-black dark:text-white w-12 shrink-0">Sub:</span>
          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email Subject" className="font-medium" disabled={isModifying || isTerminal} />
        </div>
        <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Compose your email..." className="min-h-[120px]" disabled={isModifying || isTerminal} />
      </div>

      {showMod && !isTerminal && (
        <div className="mb-4 flex gap-2">
          <Input value={modText} onChange={e => setModText(e.target.value)} placeholder="E.g., Make it more formal" autoFocus onKeyDown={e => e.key === 'Enter' && handleInlineModify()} disabled={isModifying} />
          <Button variant="secondary" onClick={handleInlineModify} disabled={isModifying}>{isModifying ? 'Updating...' : 'Ask AI'}</Button>
        </div>
      )}

      {!isTerminal && (
        <div className="flex justify-between border-t border-[#e5e5e5] dark:border-gray-800 pt-4 mt-4">
            <Button variant="secondary" onClick={() => setShowMod(!showMod)} disabled={isModifying}>Ask AI</Button>
            <div className="flex gap-2">
                <Button variant="ghost" onClick={() => onReject?.(toolApproval)} disabled={isModifying}>Cancel</Button>
                <Button variant="primary" onClick={() => onApprove?.(toolApproval, { to, subject, body })} disabled={isModifying}>Send Email</Button>
            </div>
        </div>
      )}
    </ActionCardWrapper>
  );
};

// 2. Calendar Event Create Card
export const CalendarEventCreateCard: React.FC<SpecificCardProps> = ({ toolApproval, onApprove, onReject }) => {
  // Initial parsing logic
  const initialStart = toolApproval.args.start_time || toolApproval.args.start || '';
  const initialEnd = toolApproval.args.end_time || toolApproval.args.end || '';
  
  // Helper to split ISO to date/time
  // Helper to robustly split ISO to date/time
  const splitISO = (iso: string) => {
    if (!iso) return { d: '', t: '' };
    // Regex to find YYYY-MM-DD
    const dMatch = iso.match(/(\d{4}-\d{2}-\d{2})/);
    // Regex to find HH:mm
    const tMatch = iso.match(/(\d{2}:\d{2})/);
    
    return { 
      d: dMatch ? dMatch[1] : '', 
      t: tMatch ? tMatch[1] : '' 
    };
  };

  const startParsed = splitISO(initialStart);
  const endParsed = splitISO(initialEnd);
  
  const [title, setTitle] = useState(toolApproval.args.summary || toolApproval.args.title || 'New Event');
  const [date, setDate] = useState(formatDateForInput(toolApproval.args.date || startParsed.d || ''));
  const [time, setTime] = useState(toolApproval.args.time || startParsed.t || '');
  const [endTime, setEndTime] = useState(endParsed.t || '');
  const [recurrence, setRecurrence] = useState(toolApproval.args.recurrence || '');
  const [location, setLocation] = useState(toolApproval.args.location || '');
  const [description, setDescription] = useState(toolApproval.args.description || '');
  const [modText, setModText] = useState('');
  const [showMod, setShowMod] = useState(false);
  const [isModifying, setIsModifying] = useState(false);

  const [isExpanded, setIsExpanded] = useState(toolApproval.status === 'pending');
  const isTerminal = toolApproval.status === 'executed' || toolApproval.status === 'rejected' || toolApproval.status === 'approved';

  // Extract result URL for completed state
  const resultUrl = toolApproval.result?.match(/(https?:\/\/[^\s]+)/)?.[1] || '';

  const handleInlineModify = async () => {
    if (!modText.trim()) return;
    setIsModifying(true);
    try {
      // Merge current date/time to send to AI
      const currentST = date && time ? `${date}T${time}` : (toolApproval.args.start_time || '');
      const currentET = date && endTime ? `${date}T${endTime}` : (toolApproval.args.end_time || '');
      const newArgs = await api.modifyCardParams(toolApproval.toolName, { summary: title, start_time: currentST, end_time: currentET, location, description, recurrence }, modText);
      
      if (newArgs.summary !== undefined) setTitle(newArgs.summary);
      if (newArgs.title !== undefined) setTitle(newArgs.title);
      
      const newStart = newArgs.start_time || newArgs.start || '';
      if (newStart) {
        const { d, t } = splitISO(newStart);
        if (d) setDate(d);
        if (t) setTime(t);
      } else {
        if (newArgs.date !== undefined) setDate(newArgs.date);
        if (newArgs.time !== undefined) setTime(newArgs.time);
      }
      
      const newEnd = newArgs.end_time || newArgs.end || '';
      if (newEnd) {
        const { t } = splitISO(newEnd);
        if (t) setEndTime(t);
      }
      
      if (newArgs.location !== undefined) setLocation(newArgs.location);
      if (newArgs.description !== undefined) setDescription(newArgs.description);
      if (newArgs.recurrence !== undefined) setRecurrence(newArgs.recurrence);
      setModText('');
      setShowMod(false);
    } catch (e) {
      console.error("Modify error:", e);
    } finally {
      setIsModifying(false);
    }
  };

  const handleApprove = () => {
    // Reconstruct start_time and end_time
    // backend expects ISO or simple Date string
    const safeTime = time || '00:00';
    const finalStart = date ? `${date}T${safeTime}:00` : '';
    
    let finalEnd = '';
    if (endTime) {
       finalEnd = date ? `${date}T${endTime}:00` : '';
    } else {
        // Default end time to 1 hour later if only start is known
        const dtStart = new Date(finalStart);
        if (!isNaN(dtStart.getTime())) {
            const dtEnd = new Date(dtStart.getTime() + 60 * 60 * 1000);
            const pad = (n: number) => n.toString().padStart(2, '0');
            finalEnd = `${dtEnd.getFullYear()}-${pad(dtEnd.getMonth() + 1)}-${pad(dtEnd.getDate())}T${pad(dtEnd.getHours())}:${pad(dtEnd.getMinutes())}:${pad(dtEnd.getSeconds())}`;
        } else {
            finalEnd = date || '';
        }
    }

    onApprove?.(toolApproval, { 
       summary: title, 
       start_time: finalStart, 
       end_time: finalEnd,
       location, 
       description,
       recurrence: recurrence || undefined,
       user_id: toolApproval.args.user_id // Preserve user_id if present
    });
  };

  return (
    <ActionCardWrapper 
        title="Create Calendar Event" 
        icon={<Icons.GoogleCalendar />} 
        status={toolApproval.status} 
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
    >
      <div className={`space-y-3 mb-6 transition-all duration-300 ${isModifying ? "opacity-50 pointer-events-none" : ""}`}>
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Event Title" className="font-semibold text-lg" disabled={isModifying || isTerminal} />
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-black dark:text-white w-10">Date:</span>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} disabled={isModifying || isTerminal} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-black dark:text-white w-10">Start:</span>
            <Input type="time" value={time} onChange={e => setTime(e.target.value)} disabled={isModifying || isTerminal} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-black dark:text-white w-10">End:</span>
            <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} placeholder="End time" disabled={isModifying || isTerminal} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-black dark:text-white w-10">Repeat:</span>
            <select 
              value={recurrence} 
              onChange={e => setRecurrence(e.target.value)}
              className="w-full bg-white dark:bg-[#050505] border border-[#e5e5e5] dark:border-gray-800 rounded-lg px-3 py-2 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white transition-colors disabled:opacity-50"
              disabled={isModifying || isTerminal}
            >
              <option value="">No Repeat</option>
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
              <option value="YEARLY">Yearly</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-black dark:text-white w-16">Location:</span>
          <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="Event location or link" disabled={isModifying || isTerminal} />
        </div>
        <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description..." className="min-h-[80px]" disabled={isModifying || isTerminal} />
      </div>



      {showMod && !isTerminal && (
        <div className="mb-4 flex gap-2">
          <Input value={modText} onChange={e => setModText(e.target.value)} placeholder="E.g., Move it to next tuesday" autoFocus onKeyDown={e => e.key === 'Enter' && handleInlineModify()} disabled={isModifying} />
          <Button variant="secondary" onClick={handleInlineModify} disabled={isModifying}>{isModifying ? 'Updating...' : 'Ask AI'}</Button>
        </div>
      )}

      {!isTerminal && (
        <div className="flex justify-between border-t border-[#e5e5e5] dark:border-gray-800 pt-4 mt-4">
            <Button variant="secondary" onClick={() => setShowMod(!showMod)} disabled={isModifying}>Ask AI</Button>
            <div className="flex gap-2">
                <Button variant="ghost" onClick={() => onReject?.(toolApproval)} disabled={isModifying}>Cancel</Button>
                <Button variant="primary" onClick={handleApprove} disabled={isModifying}>Create Event</Button>
            </div>
        </div>
      )}
    </ActionCardWrapper>
  );
};

// 3. Google Docs Card
export const GoogleDocsCard: React.FC<SpecificCardProps> = ({ toolApproval, onApprove, onReject }) => {
  const [title, setTitle] = useState(toolApproval.args.title || 'Untitled Document');
  const [content, setContent] = useState(toolApproval.args.initial_text || toolApproval.args.content || toolApproval.args.text || '');
  const [modText, setModText] = useState('');
  const [showMod, setShowMod] = useState(false);
  const [isModifying, setIsModifying] = useState(false);

  const [isExpanded, setIsExpanded] = useState(toolApproval.status === 'pending');
  const isTerminal = toolApproval.status === 'executed' || toolApproval.status === 'rejected' || toolApproval.status === 'approved';

  const handleInlineModify = async () => {
    if (!modText.trim()) return;
    setIsModifying(true);
    try {
      const newArgs = await api.modifyCardParams(toolApproval.toolName, { title, content }, modText);
      if (newArgs.title !== undefined) setTitle(newArgs.title);
      if (newArgs.content !== undefined) setContent(newArgs.content);
      if (newArgs.initial_text !== undefined) setContent(newArgs.initial_text);
      if (newArgs.text !== undefined) setContent(newArgs.text);
      setModText('');
      setShowMod(false);
    } catch (e) {} finally {
      setIsModifying(false);
    }
  };

  // Extract result URL for completed state
  const resultUrl = toolApproval.result?.match(/URL:\s*(https?:\/\/[^\s]+)/)?.[1] || 
                   toolApproval.result?.match(/(https?:\/\/docs\.google\.com\/document\/[^\s]+)/)?.[1] || '';

  return (
    <ActionCardWrapper 
        title="Document Draft" 
        icon={<Icons.GoogleDocs />} 
        status={toolApproval.status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
    >
      <div className={`space-y-3 mb-6 transition-all duration-300 ${isModifying ? "opacity-50 pointer-events-none" : ""}`}>
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Document Title" className="font-bold text-xl border-none !px-0" disabled={isModifying || isTerminal} />
        <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Write your document content here..." className="min-h-[250px] font-serif" disabled={isModifying || isTerminal} />
      </div>



      {showMod && !isTerminal && (
        <div className="mb-4 flex gap-2">
          <Input value={modText} onChange={e => setModText(e.target.value)} placeholder="E.g., Make it sound more professional" autoFocus onKeyDown={e => e.key === 'Enter' && handleInlineModify()} disabled={isModifying} />
          <Button variant="secondary" onClick={handleInlineModify} disabled={isModifying}>{isModifying ? 'Updating...' : 'Ask AI'}</Button>
        </div>
      )}

      {!isTerminal && (
        <div className="flex justify-between border-t border-[#e5e5e5] dark:border-gray-800 pt-4 mt-4">
          <Button variant="secondary" onClick={() => setShowMod(!showMod)} disabled={isModifying}>Ask AI</Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onReject?.(toolApproval)} disabled={isModifying}>Cancel</Button>
            <Button variant="primary" onClick={() => onApprove?.(toolApproval, { title, initial_text: content, content })} disabled={isModifying}>Save Document</Button>
          </div>
        </div>
      )}
    </ActionCardWrapper>
  );
};

// 4. Email Schedule Card
export const EmailScheduleCard: React.FC<SpecificCardProps> = ({ toolApproval, onApprove, onReject }) => {
  const [to, setTo] = useState(toolApproval.args.to || toolApproval.args.recipient || toolApproval.args.email || '');
  const [subject, setSubject] = useState(toolApproval.args.subject || toolApproval.args.title || '');
  const [body, setBody] = useState(toolApproval.args.body || toolApproval.args.message || toolApproval.args.content || toolApproval.args.text || '');

  // Parse send_at for date and time inputs
  const initialSendAt = toolApproval.args.send_at || '';
  const initialDate = initialSendAt ? initialSendAt.split('T')[0] : '';
  const initialTime = initialSendAt ? initialSendAt.split('T')[1]?.substring(0, 5) : '';

  const [sendDate, setSendDate] = useState(formatDateForInput(initialDate));
  const [sendTime, setSendTime] = useState(initialTime);

  const [modText, setModText] = useState('');
  const [showMod, setShowMod] = useState(false);
  const [isModifying, setIsModifying] = useState(false);

  const [isExpanded, setIsExpanded] = useState(toolApproval.status === 'pending');
  const isTerminal = toolApproval.status === 'executed' || toolApproval.status === 'rejected' || toolApproval.status === 'approved';

  const handleInlineModify = async () => {
    if (!modText.trim()) return;
    setIsModifying(true);
    try {
      const currentSendAt = sendDate && sendTime ? `${sendDate}T${sendTime}:00` : '';
      const newArgs = await api.modifyCardParams(toolApproval.toolName, { to, subject, body, send_at: currentSendAt }, modText);
      
      if (newArgs.to !== undefined) setTo(newArgs.to);
      if (newArgs.subject !== undefined) setSubject(newArgs.subject);
      if (newArgs.body !== undefined) setBody(newArgs.body);
      
      if (newArgs.send_at !== undefined) {
        const newDate = newArgs.send_at.split('T')[0];
        const newTime = newArgs.send_at.split('T')[1]?.substring(0, 5);
        setSendDate(newDate);
        setSendTime(newTime);
      }
      setModText('');
      setShowMod(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsModifying(false);
    }
  };

  const handleApprove = () => {
    const send_at = sendDate && sendTime ? `${sendDate}T${sendTime}:00` : '';
    onApprove?.(toolApproval, { to, subject, body, send_at });
  };

  return (
    <ActionCardWrapper 
        title="Schedule Email" 
        icon={<Icons.Gmail />} 
        status={toolApproval.status} 
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
    >
      <div className={`space-y-3 mb-6 transition-all duration-300 ${isModifying ? "opacity-50 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-3">
          <span className="text-sm text-black dark:text-white w-12 shrink-0">To:</span>
          <Input value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@example.com" disabled={isModifying || isTerminal} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-black dark:text-white w-12 shrink-0">Sub:</span>
          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email Subject" className="font-medium" disabled={isModifying || isTerminal} />
        </div>
        <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Compose your email..." className="min-h-[120px]" disabled={isModifying || isTerminal} />
        
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-black dark:text-white w-10">Date:</span>
            <Input type="date" value={sendDate} onChange={e => setSendDate(e.target.value)} disabled={isModifying || isTerminal} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-black dark:text-white w-10">Time:</span>
            <Input type="time" value={sendTime} onChange={e => setSendTime(e.target.value)} disabled={isModifying || isTerminal} />
          </div>
        </div>
      </div>

      {showMod && !isTerminal && (
        <div className="mb-4 flex gap-2">
          <Input value={modText} onChange={e => setModText(e.target.value)} placeholder="E.g., Schedule for tomorrow morning" autoFocus onKeyDown={e => e.key === 'Enter' && handleInlineModify()} disabled={isModifying} />
          <Button variant="secondary" onClick={handleInlineModify} disabled={isModifying}>{isModifying ? 'Updating...' : 'Ask AI'}</Button>
        </div>
      )}

      {!isTerminal && (
        <div className="flex justify-between border-t border-[#e5e5e5] dark:border-gray-800 pt-4 mt-4">
            <Button variant="secondary" onClick={() => setShowMod(!showMod)} disabled={isModifying}>Ask AI</Button>
            <div className="flex gap-2">
                <Button variant="ghost" onClick={() => onReject?.(toolApproval)} disabled={isModifying}>Cancel</Button>
                <Button variant="primary" onClick={handleApprove} disabled={isModifying}>Schedule Email</Button>
            </div>
        </div>
      )}
    </ActionCardWrapper>
  );
};


const getIconForTool = (toolName: string) => {
  const low = (toolName || '').toLowerCase();
  if (low.includes('gmail') || low.includes('email')) return Icons.Gmail;
  if (low.includes('calendar') || low.includes('event')) return Icons.GoogleCalendar;
  if (low.includes('doc')) return Icons.GoogleDocs;
  if (low.includes('sheet') || low.includes('spreadsheet')) return Icons.GoogleSheets;
  if (low.includes('drive')) return Icons.GoogleDrive;
  return Icons.Logo;
};

const TOOL_DISPLAY_NAMES: { [key: string]: string } = {
  'gmail_send': 'Send Email',
  'send_email': 'Send Email',
  'create_event': 'Create Calendar Event',
  'add_event': 'Create Calendar Event',
  'schedule_email': 'Schedule Email',
  'search_docs': 'Document Search',
  'get_doc_content': 'Reading Document',
  'read_sheet_values': 'Read Sheet Data',
  'search_sheet': 'Searching Spreadsheet',
  'create_spreadsheet': 'Create Spreadsheet',
  'create_doc': 'Create Document',
  // Add other specific tool names here if their default formatting is not good
};



export const SystemPipelineCard: React.FC<any> = ({ toolApproval, onApprove, onReject, ...rest }) => {
  const [localPhase, setLocalPhase] = React.useState<'pending' | 'source_executing' | 'target_executing' | 'executed' | 'rejected' | 'failed'>(
    toolApproval.status === 'approved' ? 'source_executing' : toolApproval.status as any
  );

  React.useEffect(() => {
    if (toolApproval.status === 'pending') setLocalPhase('pending');
    else if (toolApproval.status === 'approved') {
       setLocalPhase('source_executing');
       const t = setTimeout(() => setLocalPhase('target_executing'), 2000);
       return () => clearTimeout(t);
    }
    else if (toolApproval.status === 'executed') {
       setLocalPhase('executed');
    }
    else if (toolApproval.status === 'rejected' || toolApproval.status === 'failed') {
       setLocalPhase(toolApproval.status as any);
    }
  }, [toolApproval.status]);

  const args = toolApproval.args || {};
  const source = typeof args.source === 'string' ? args.source : 'unknown';
  const target = typeof args.target === 'string' ? args.target : 'unknown';

  const sourceName = source === 'gmail' ? 'Gmail' :
                     source === 'sheets' ? 'Google Sheets' :
                     source === 'notion' ? 'Notion' :
                     source === 'docs' ? 'Google Docs' :
                     source === 'hubspot' ? 'HubSpot' : source.replace(/_/g, ' ');

  const targetName = target === 'sheets' ? 'Google Sheets' :
                     target === 'notion' ? 'Notion' :
                     target === 'docs' ? 'Google Docs' : 
                     target === 'gmail' ? 'Gmail' :
                     target === 'hubspot' ? 'HubSpot' : target.replace(/_/g, ' ');

  // Map sources to tools
  const sourceToolName = source === 'gmail' ? 'search_email' :
                         source === 'sheets' ? 'search_sheet' :
                         source === 'notion' ? 'search_notion' :
                         source === 'docs' ? 'read_google_doc' :
                         source === 'hubspot' ? 'search_hubspot' : `${source}_read`;

  const targetToolName = target === 'sheets' ? 'append_sheet_values' :
                         target === 'notion' ? 'create_notion_page' :
                         target === 'docs' ? 'update_google_doc' :
                         target === 'gmail' ? 'send_email' : `${target}_write`;

  // Parse result if executed
  let sourceResult = undefined;
  let targetResult = undefined;
  if (localPhase === 'executed' && toolApproval.result) {
     sourceResult = toolApproval.result; 
     targetResult = toolApproval.result; 
  }

  const sourceApproval: any = {
     ...toolApproval,
     id: toolApproval.id + '_source',
     toolName: sourceToolName,
     args: args.source_config || {},
     status: localPhase === 'pending' ? 'pending' : localPhase === 'source_executing' ? 'approved' : (localPhase === 'rejected' || localPhase === 'failed') ? localPhase : 'executed',
     result: localPhase === 'executed' ? sourceResult : undefined
  };

  const targetApproval: any = {
     ...toolApproval,
     id: toolApproval.id + '_target',
     toolName: targetToolName,
     args: args.target_config || {},
     status: (localPhase === 'pending' || localPhase === 'source_executing') ? 'pending' : localPhase === 'target_executing' ? 'approved' : (localPhase === 'rejected' || localPhase === 'failed') ? localPhase : 'executed',
     result: localPhase === 'executed' ? targetResult : undefined
  };

  // Wrap onApprove to pass the original toolApproval, NOT the sourceApproval
  const handleApprove = (_app: any, _args: any) => {
      if (onApprove) onApprove?.(toolApproval, toolApproval.args);
  };
  
  const handleReject = (_app: any) => {
      if (onReject) onReject?.(toolApproval);
  };

  return (
    <div className="flex flex-col items-center w-full">
       <DefaultActionCard toolApproval={sourceApproval} onApprove={localPhase === 'pending' ? (handleApprove as any) : undefined} onReject={localPhase === 'pending' ? (handleReject as any) : undefined} />
       
       {localPhase !== 'pending' && localPhase !== 'source_executing' && (
           <div className="text-[12px] font-mono text-gray-500 dark:text-gray-400 my-2 text-center bg-gray-50 dark:bg-[#111] px-4 py-1.5 rounded-full border border-gray-100 dark:border-gray-800 animate-in fade-in slide-in-from-top-1">
             ↓ Fetched {sourceName} data, now writing into {targetName} ↓
           </div>
       )}

       {(localPhase === 'target_executing' || localPhase === 'executed') && (
           <div className="w-full animate-in fade-in slide-in-from-top-2">
             <DefaultActionCard toolApproval={targetApproval} onApprove={undefined} onReject={undefined} />
           </div>
       )}
    </div>
  )
}


// Default Fallback Form for other pending tools
export const DefaultActionCard: React.FC<SpecificCardProps> = ({ toolApproval, onApprove, onReject }) => {
  const [isExpanded, setIsExpanded] = useState(toolApproval.status === 'pending');
  const isTerminal = toolApproval.status === 'executed' || toolApproval.status === 'rejected' || toolApproval.status === 'approved';

  const toolName = toolApproval.toolName || 'unknown_tool';
  const displayName = TOOL_DISPLAY_NAMES[toolName.toLowerCase()] || toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const ToolIcon = getIconForTool(toolName);

  // Classify args by importance
  const classifiedArgs = classifyVisibleArgs(toolApproval.args || {});
  const actionSummary = getActionSummary(toolName, toolApproval.args || {});

  // Build a clean description
  const descText = toolApproval.description && !toolApproval.description.startsWith('Execute ') 
    ? toolApproval.description 
    : null;

  return (
    <ActionCardWrapper 
        title={displayName} 
        icon={<ToolIcon />} 
        status={toolApproval.status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
    >
      {/* Action Summary Banner */}
      {actionSummary && !isTerminal && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-gray-50/50 dark:bg-[#0a0a0a]/30 rounded-xl border border-gray-100 dark:border-gray-800">
          <div className="w-8 h-8 rounded-lg bg-white dark:bg-[#171717] border border-gray-200/80 dark:border-gray-800/80 flex items-center justify-center shadow-sm shrink-0">
            <Icons.ShieldAlert className="w-4 h-4 text-black dark:text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-black dark:text-white truncate">{actionSummary}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Requires approval before execution</p>
          </div>
        </div>
      )}

      {/* Description */}
      {descText && (
        <p className="text-[14px] text-[#202124] dark:text-[#ececec] mb-4 leading-relaxed px-1">
          {descText}
        </p>
      )}

      {/* Importance-classified Parameters in a unified clean card list */}
      {classifiedArgs.length > 0 && (
        <div className="border border-gray-200/80 dark:border-gray-800/85 rounded-xl bg-gray-50/20 dark:bg-[#050505]/10 divide-y divide-gray-150/40 dark:divide-gray-800/60 overflow-hidden mb-4 shadow-sm">
          {classifiedArgs.map(({ importance, key, value }) => {
            const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
            const isLongValue = displayValue.length > 100;
            
            return (
              <div 
                key={key} 
                className="p-3.5 flex items-start gap-4 transition-colors hover:bg-gray-50/30 dark:hover:bg-[#111]/10"
              >
                <div className="w-24 shrink-0 flex flex-col justify-start">
                  <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {getFriendlyLabel(key)}
                  </span>
                  {importance === 'critical' && (
                    <span className="inline-flex mt-1.5 w-fit text-[8px] font-bold bg-black dark:bg-white text-white dark:text-black px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0">
                      Critical
                    </span>
                  )}
                  {importance === 'important' && (
                    <span className="inline-flex mt-1.5 w-fit text-[8px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0">
                      Review
                    </span>
                  )}
                </div>
                
                {isLongValue ? (
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-[#202124] dark:text-[#ececec] font-mono whitespace-pre-wrap bg-white dark:bg-[#0a0a0a]/30 border border-gray-100 dark:border-gray-800 rounded-lg p-3 leading-relaxed max-h-[160px] overflow-y-auto">
                      {displayValue}
                    </div>
                  </div>
                ) : (
                  <div className={`flex-1 min-w-0 text-[13.5px] text-[#202124] dark:text-[#ececec] truncate leading-normal self-center ${
                    importance === 'critical' ? 'font-semibold text-black dark:text-white' : 'font-medium'
                  }`}>
                    {displayValue}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* No visible args and no description — show a simple ready state */}
      {!descText && classifiedArgs.length === 0 && (
        <div className="bg-gray-50/50 dark:bg-[#050505]/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Icons.Check className="w-4 h-4" />
            <span>Ready to execute — no configuration needed.</span>
          </div>
        </div>
      )}

      {/* Clean Execution Result */}
      {toolApproval.status === 'executed' && toolApproval.result && (
        <ExecutionResultDisplay result={toolApproval.result} toolName={toolName} />
      )}

      {!isTerminal && (
        <div className="flex justify-end gap-3 border-t border-gray-50 dark:border-gray-800 pt-5">
            <Button variant="ghost" onClick={() => onReject?.(toolApproval)}>Cancel</Button>
            <Button variant="primary" onClick={() => onApprove?.(toolApproval, toolApproval.args)}>Confirm Action</Button>
        </div>
      )}
    </ActionCardWrapper>
  );
};


// Result Cards (Data display instead of forms)

interface ListResultCardProps {
  items: any[];
  onAction?: (actionText: string) => void;
}

export const EmailReadCard: React.FC<{ email: any }> = ({ email }) => {
  if (!email) return null;
  return (
    <ActionCardWrapper title="Email Details" icon={<Icons.Gmail />} status="executed">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 border-b border-gray-50 dark:border-gray-800 pb-4">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-black dark:text-white uppercase tracking-wider w-12">From:</span>
            <span className="text-sm font-semibold text-black dark:text-white">{email.from || 'Unknown'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-black dark:text-white uppercase tracking-wider w-12">To:</span>
            <span className="text-sm text-black dark:text-white">{email.to || 'Unknown'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-black dark:text-white uppercase tracking-wider w-12">Date:</span>
            <span className="text-sm text-black dark:text-white">{email.date || 'Unknown'}</span>
          </div>
        </div>
        
        <div className="py-2">
          <h2 className="text-lg font-bold text-black dark:text-white mb-4">{email.subject || '(No Subject)'}</h2>
          <div className="text-[15px] text-black dark:text-white leading-relaxed whitespace-pre-wrap bg-gray-50/30 dark:bg-[#050505]/30 p-4 rounded-xl border border-gray-50 dark:border-gray-800">
            {email.body || email.snippet || '(Empty)'}
          </div>
        </div>
      </div>
    </ActionCardWrapper>
  );
};

export const EmailListCard: React.FC<ListResultCardProps> = ({ items, onAction }) => {
  return (
    <ActionCardWrapper title="Email List" icon={<Icons.Gmail />} status="executed">
      <div className="flex flex-col -mx-5 -mb-5">
        {items.map((item, i) => {
          const sender = item.sender || item.from || 'Unknown';
          // Clean up sender name from "Name <email@gmail.com>" format
          const senderName = sender.split('<')[0].trim() || sender.split('@')[0];
          
          // Truncate sender name if it looks like an email and is too long
          const displaySender = senderName.length > 20 ? senderName.substring(0, 17) + '...' : senderName;

          return (
            <div 
              key={i} 
              className="flex items-center gap-4 px-5 py-3.5 border-b border-gray-100 dark:border-white/5 last:border-0 hover:bg-gray-50/80 dark:hover:bg-gray-900/30 transition-colors cursor-pointer group"
              onClick={() => onAction && onAction(`Read email ${item.id}`)}
            >
               <div className="w-32 md:w-40 shrink-0 font-bold text-[#202124] dark:text-[#ececec] text-[13px] truncate">
                  {displaySender}
               </div>
               <div className="flex-1 min-w-0 text-[13px] text-[#202124] dark:text-[#ececec] truncate">
                  <span className="font-semibold">{item.subject || '(No Subject)'}</span>
                  {item.snippet && <span className="text-black dark:text-gray-400 font-normal"> - {item.snippet}</span>}
               </div>
               <div className="w-16 shrink-0 text-right text-[12px] font-bold text-[#202124] dark:text-[#ececec]">
                  {item.time || item.date || ''}
               </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-12 px-5">
            <div className="w-12 h-12 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
              <Icons.Gmail className="w-6 h-6 text-black dark:text-white" />
            </div>
            <div className="text-sm font-medium text-black dark:text-white">No emails found in your inbox.</div>
          </div>
        )}
      </div>
    </ActionCardWrapper>
  );
};
export const CalendarEventListCard: React.FC<ListResultCardProps> = ({ items, onAction }) => {
  return (
    <ActionCardWrapper title="Calendar Events" icon={<Icons.GoogleCalendar />} status="executed">
      <div className="flex flex-col -mx-5 -mb-5">
        {items.map((item, i) => {
          const title = item.title || item.summary || 'Untitled Event';
          const date = item.date || item.start?.date || item.start?.dateTime || '';
          const displayDate = date.includes('T') ? date.split('T')[0] : date;
          
          return (
            <div 
              key={i} 
              className="flex items-center gap-4 px-5 py-3.5 border-b border-gray-100 dark:border-white/5 last:border-0 hover:bg-gray-50/80 dark:hover:bg-gray-900/30 transition-colors cursor-pointer group"
              onClick={() => onAction && onAction(`View event ${item.id || title}`)}
            >
               <div className="flex-1 min-w-0 font-bold text-[#202124] dark:text-[#ececec] text-[15px] truncate">
                  {title}
               </div>
               <div className="w-32 shrink-0 text-right text-[12px] font-bold text-[#202124] dark:text-[#ececec]">
                  {displayDate}
               </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-12 px-5 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
              <Icons.GoogleCalendar className="w-6 h-6 text-black dark:text-white" />
            </div>
            <div className="text-sm font-medium text-black dark:text-white">No upcoming events found.</div>
          </div>
        )}
      </div>
    </ActionCardWrapper>
  );
};

export const GoogleSheetsCard: React.FC<ListResultCardProps> = ({ items, onAction }) => {
  if (items.length === 0) {
    return (
       <ActionCardWrapper title="Sheet Data" icon={<Icons.GoogleSheets />} status="executed">
         <div className="text-sm text-black dark:text-white py-8 text-center">No data found in this sheet.</div>
       </ActionCardWrapper>
    );
  }

  const is2DArray = Array.isArray(items[0]);
  let headers: string[] = [];
  let rows: any[] = [];

  if (is2DArray) {
     headers = items[0] || [];
     rows = items.slice(1);
  } else {
     headers = Object.keys(items[0]);
     rows = items.map(r => Object.values(r));
  }

  return (
    <ActionCardWrapper title="Sheet View" icon={<Icons.GoogleSheets />} status="executed">
      <div className="max-w-full overflow-x-auto -mx-5 -mb-5 border-t border-gray-100 dark:border-white/5">
         <table className="w-full text-left min-w-[500px] border-collapse">
            <thead className="bg-gray-50/50 dark:bg-gray-900/50">
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className="p-4 font-bold text-[#202124] dark:text-[#ececec] text-[12px] uppercase tracking-wider border-b border-gray-100 dark:border-white/5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/5">
               {rows.map((row, i) => (
                 <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-gray-900/30 transition-colors">
                   {row.map((cell: any, j: number) => (
                     <td key={j} className="p-4 text-[14px] text-black dark:text-white max-w-[240px] truncate">{String(cell)}</td>
                   ))}
                 </tr>
               ))}
            </tbody>
         </table>
      </div>
    </ActionCardWrapper>
  );
};

export const UpgradeCard: React.FC<{ targetPlan: string, onUpgrade: () => void }> = ({ targetPlan, onUpgrade }) => {
  return (
    <div className="bg-black text-white rounded-[20px] p-6 shadow-2xl flex flex-col gap-5 animate-in slide-in-from-bottom-4 duration-500 overflow-hidden relative max-w-[720px] mx-auto">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-gray-500/20 rounded-full blur-3xl"></div>
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Icons.Coins className="w-5 h-5 text-white" />
            </div>
            <div>
                <h3 className="text-lg font-bold">Out of Credit Limit</h3>
                <p className="text-xs text-gray-400">Upgrade to {targetPlan} to continue</p>
            </div>
        </div>
        
        <p className="text-sm text-gray-300 leading-relaxed">
            You are out of credit limit. Please upgrade to continue building your workflows and automating your tasks.
        </p>

        <ul className="space-y-2">
            <li className="flex items-center gap-2 text-xs text-white">
                <Icons.Check className="w-3.5 h-3.5 text-white" />
                <span>{targetPlan === 'Pro' ? '2,000 monthly credits' : '25,000 monthly credits'}</span>
            </li>
            <li className="flex items-center gap-2 text-xs text-white">
                <Icons.Check className="w-3.5 h-3.5 text-white" />
                <span>Unlimited app connections</span>
            </li>
        </ul>

        <button 
            onClick={onUpgrade}
            className="w-full py-3.5 bg-white text-black font-bold text-sm rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
        >
            View Pricing
            <Icons.ChevronDown className="w-4 h-4 -rotate-90" />
        </button>
    </div>
  );
};

export const AuthRequiredCard: React.FC<{ appName: string, onReconnect: () => void }> = ({ appName, onReconnect }) => {
  return (
    <div className="bg-white border-2 border-gray-100 rounded-[20px] p-6 shadow-xl flex flex-col gap-4 animate-in slide-in-from-bottom-4 duration-500 overflow-hidden relative max-w-[720px] mx-auto">
        <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
                <Icons.ShieldAlert className="w-6 h-6 text-black" />
            </div>
            <div>
                <h3 className="text-lg font-bold text-black">Connection Expired</h3>
                <p className="text-sm text-black">Your {appName} access has expired</p>
            </div>
        </div>
        
        <p className="text-[14px] text-black leading-relaxed bg-gray-50 p-4 rounded-xl border border-gray-200">
            Google has revoked or expired your access token for this workspace. To continue using {appName} tools, please reconnect your account in the Integrations menu.
        </p>

        <div className="flex gap-3 mt-2">
            <button 
                onClick={onReconnect}
                className="flex-1 py-3 bg-black text-white font-bold text-sm rounded-xl hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
            >
                Reconnect {appName}
                <Icons.ChevronDown className="w-4 h-4 -rotate-90" />
            </button>
        </div>
    </div>
  );
};

// Main Routing Component
interface ActionCardProps {
  toolApproval?: ToolApproval; // For pending actions
  resultData?: any; // For displaying results like JSON arrays
  resultTool?: string; // e.g. 'list_emails'
  onApprove?: (approval: ToolApproval, args: any) => void;
  onReject?: (approval: ToolApproval) => void;
  onModifyAction?: (text: string) => void; // "Modify this action based on user edits: "
  onSendMessage?: (text: string) => void; // Trigger a new chat message
  onOpenPricing?: () => void;
  onOpenIntegrations?: () => void;
}

export const ActionCard: React.FC<ActionCardProps> = ({ 
  toolApproval, 
  resultData: propResultData, 
  resultTool: propResultTool, 
  onApprove, 
  onReject, 
  onSendMessage,
  onOpenPricing,
  onOpenIntegrations
}) => {
  // 1. Data Context Extraction
  let effectiveResultData = propResultData;
  let effectiveToolName = (propResultTool || toolApproval?.toolName || 'unknown').toLowerCase();
  let effectiveListArray: any[] | null = null;
  
  // Auto-extract result from executed tool if not provided
  if (!effectiveResultData && toolApproval?.result && toolApproval.status === 'executed') {
     try {
        const parsed = JSON.parse(toolApproval.result);
        if (Array.isArray(parsed) || (typeof parsed === 'object' && parsed !== null)) {
            effectiveResultData = parsed;
        }
     } catch(e) {
        effectiveResultData = toolApproval.result;
     }
  }

  // Generic Array Extractor for Wrapper Objects (e.g. { count: 10, items: [...] })
  if (effectiveResultData) {
      if (Array.isArray(effectiveResultData)) {
          effectiveListArray = effectiveResultData;
      } else if (typeof effectiveResultData === 'object' && effectiveResultData !== null) {
          const arrayVals = Object.values(effectiveResultData).filter(val => Array.isArray(val));
          if (arrayVals.length > 0) {
              // Find the largest array (most likely to be the main data list)
              effectiveListArray = arrayVals.sort((a: any, b: any) => b.length - a.length)[0] as any[];
          }
      }
  }

  // 2. High-Level Routing
  
  // B. Action Forms take precedence if toolApproval is provided
  // This ensures the "rectangular box" of the action stays persistent.
  if (toolApproval && onApprove && onReject) {
     const tName = effectiveToolName;

      if (tName === 'execute_system_pipeline') {
          return <SystemPipelineCard toolApproval={toolApproval} onApprove={onApprove} onReject={onReject} />;
      }

      if (tName.includes('upgrade') || tName.includes('billing') || tName.includes('pricing')) {
         return <UpgradeCard targetPlan={toolApproval.args.target || 'Pro'} onUpgrade={onOpenPricing || (() => {})} />;
      }

      if (toolApproval.status === 'auth_required') {
          const appName = effectiveToolName.includes('gmail') || effectiveToolName.includes('email') ? 'Gmail' :
                          effectiveToolName.includes('calendar') ? 'Google Calendar' :
                          effectiveToolName.includes('docs') ? 'Google Docs' :
                          effectiveToolName.includes('sheets') ? 'Google Sheets' : 'Google Workspace';
          return <AuthRequiredCard appName={appName} onReconnect={() => onOpenIntegrations ? onOpenIntegrations() : window.location.href = '/integrations'} />;
      }

     const isEmailAction = (tName.includes('email') || tName.includes('gmail')) && (tName.includes('send') || tName.includes('schedule') || tName.includes('draft') || tName.includes('create'));
     if (isEmailAction) {
        if (tName.includes('schedule')) return <EmailScheduleCard toolApproval={toolApproval} onApprove={onApprove} onReject={onReject} />;
        return <EmailSendCard toolApproval={toolApproval} onApprove={onApprove} onReject={onReject} />;
     }

     const isCalendarAction = (tName.includes('calendar') || tName.includes('event')) && (tName.includes('create') || tName.includes('add') || tName.includes('insert') || tName.includes('update'));
     if (isCalendarAction && !tName.includes('list') && !tName.includes('get')) {
        return <CalendarEventCreateCard toolApproval={toolApproval} onApprove={onApprove} onReject={onReject} />;
     }

      const isSheetAction = tName.includes('sheet') || tName.includes('spreadsheet');
      if (isSheetAction) {
         if (tName.includes('list') || tName.includes('search') || tName.includes('get') || (tName.includes('read') && !tName.includes('spreadsheet'))) {
            const title = tName.includes('spreadsheet') ? 'Search Spreadsheets' : 'Read Sheet Data';
            // If we have data, show the rich list
            if (effectiveResultData && Array.isArray(effectiveResultData)) {
               return <GoogleSheetsCard items={effectiveResultData} onAction={onSendMessage} />;
            }
            // Otherwise show the pending/executing card with the sheet logo
            return (
               <ActionCardWrapper title={title} icon={<Icons.GoogleSheets />} status={toolApproval.status}>
                  <div className="text-sm text-black">
                     {toolApproval.description || `Accessing Google Sheets...`}
                  </div>
                  {toolApproval.status === 'pending' && onApprove && onReject && (
                     <div className="flex gap-2 mt-4">
                        <Button onClick={() => onApprove?.(toolApproval, toolApproval.args)}>Approve Access</Button>
                        <Button variant="secondary" onClick={() => onReject?.(toolApproval)}>Cancel</Button>
                     </div>
                  )}
               </ActionCardWrapper>
            );
         }
         return <DefaultActionCard toolApproval={toolApproval} onApprove={onApprove} onReject={onReject} />;
      }
      
      const isDocAction = (tName.includes('doc') || tName.includes('file') || tName.includes('drive')) && (tName.includes('create') || tName.includes('write') || tName.includes('update') || tName.includes('insert'));
      if (isDocAction) {
         return <GoogleDocsCard toolApproval={toolApproval} onApprove={onApprove} onReject={onReject} />;
      }
      
          const hasData = !!effectiveResultData;
          const hasList = !!effectiveListArray && effectiveListArray.length > 0;
          
          const isEmail = (tName.includes('email') || tName.includes('gmail')) || 
                          (hasList && effectiveListArray![0].subject !== undefined) || 
                          (hasData && !hasList && effectiveResultData.subject !== undefined);
                          
          const isEvent = (tName.includes('calendar') || tName.includes('event')) || 
                          (hasList && (effectiveListArray![0].start !== undefined || effectiveListArray![0].summary !== undefined)) || 
                          (hasData && !hasList && effectiveResultData.summary !== undefined);

          if (isEmail && hasData) {
             if (hasList) return <EmailListCard items={effectiveListArray!} onAction={onSendMessage} />;
             if (typeof effectiveResultData === 'object') return <EmailReadCard email={effectiveResultData} />;
          }
          if (isEvent && hasData) {
             if (hasList) return <CalendarEventListCard items={effectiveListArray!} onAction={onSendMessage} />;
          }

     // Fallback to General Action Card (which now shows results inside)
     return <DefaultActionCard toolApproval={toolApproval} onApprove={onApprove} onReject={onReject} />;
  }

  // A. Standalone Data Result Displays (Lists, Sheets, etc)
  // Only used if there's no active toolApproval block or if explicitly passed as results
  if (effectiveResultData) {
      const toolCmp = effectiveToolName;
      const hasList = !!effectiveListArray && effectiveListArray.length > 0;
      
      const isEmail = toolCmp.includes('email') || toolCmp.includes('gmail') || (hasList && effectiveListArray![0].subject !== undefined);
      const isEvent = toolCmp.includes('calendar') || toolCmp.includes('event') || (hasList && (effectiveListArray![0].start !== undefined || effectiveListArray![0].summary !== undefined));
      const isSheet = toolCmp.includes('sheet') || (hasList && Array.isArray(effectiveListArray![0]));

      if (isEmail && hasList) return <EmailListCard items={effectiveListArray!} onAction={onSendMessage} />;
      if (isEvent && hasList) return <CalendarEventListCard items={effectiveListArray!} onAction={onSendMessage} />;
      if (isSheet && hasList) return <GoogleSheetsCard items={effectiveListArray!} onAction={onSendMessage} />;
      
      const displayToolName = TOOL_DISPLAY_NAMES[effectiveToolName] || effectiveToolName.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
      return (
         <ActionCardWrapper title={displayToolName} icon={<Icons.Logs />} status="executed">
            <ExecutionResultDisplay 
              result={typeof effectiveResultData === 'string' ? effectiveResultData : JSON.stringify(effectiveResultData)} 
              toolName={effectiveToolName} 
            />
         </ActionCardWrapper>
      );
  }

  return null;
};

export default ActionCard;
